/**
 * Creator context extraction utilities
 * 
 * These functions extract job-related context from various
 * creator object shapes (Creator, CreatorProfileForMyLink)
 */

import type { Creator, CreatorProfileForMyLink, CreatorJobFeedPreferences } from "@artha/types";
import type { CreatorJobContext } from "./types";

/**
 * Extract creator job context from various creator object shapes
 * 
 * Handles both Creator (backend) and CreatorProfileForMyLink (frontend) types.
 * Extracts job feed preferences and niche categories consistently.
 * 
 * @param creator - Creator object (Creator or CreatorProfileForMyLink type)
 * @returns Extracted job context with preferences and categories
 */
export function extractCreatorContext(
  creator?: Creator | CreatorProfileForMyLink | any | null
): CreatorJobContext {
  if (!creator) return {};

  const rawJobFeedPreferences =
    "settings" in creator
      ? creator.settings?.jobFeedPreferences
      : creator.jobFeedPreferences;

  // Extract job feed preferences
  // Backend Creator: creator.jobFeedPreferences
  // Frontend CreatorProfileForMyLink: creator.settings.jobFeedPreferences
  const jobFeedPreferences: CreatorJobFeedPreferences | undefined = rawJobFeedPreferences
    ? {
        ...rawJobFeedPreferences,
        // Backward/legacy fallback for public profile payload variants
        positiveKeywords:
          rawJobFeedPreferences.positiveKeywords ??
          (Array.isArray(rawJobFeedPreferences.keywords)
            ? rawJobFeedPreferences.keywords
            : undefined) ??
          (Array.isArray(rawJobFeedPreferences.jobKeywords)
            ? rawJobFeedPreferences.jobKeywords
            : undefined),
      }
    : undefined;

  const nicheCategoriesFromPreferences: string[] = jobFeedPreferences?.categories ?? [];

  const nicheCategoriesFromSettings: string[] =
    "settings" in creator
      ? creator.settings?.nicheCategories ?? []
      : creator.profile?.nicheCategories ?? [];

  const fallbackNicheTags: string[] =
    "nicheTags" in creator ? creator.nicheTags ?? [] : [];

  // Extract niche categories using a fallback chain (priority order)
  // 1. Explicitly selected categories from jobFeedPreferences (user-selected)
  // 2. Categories from creator settings or profile (AI-enriched or manually set)
  // 3. Fallback niche tags (legacy tags)
  const nicheCategories = [
    ...new Set<string>(
      nicheCategoriesFromPreferences.length > 0
        ? nicheCategoriesFromPreferences
        : nicheCategoriesFromSettings.length > 0
        ? nicheCategoriesFromSettings
        : fallbackNicheTags
    ),
  ];

  return {
    jobFeedPreferences,
    nicheCategories,
  };
}
